/* eslint-disable max-len */
import {
  Component,
  ComponentInterface,
  h,
  Prop,
  Method,
  Element,
  Listen,
  Host,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';

export interface Link {
  src: string;
  title: string;
  target?: '_self' | '_blank';
  style?: 'text' | 'button-primary' | 'button-secondary' | 'button-cta';
  icon?: string;
  iconOption?: 'icon' | 'auto' | 'none';
  download?: string;
  delay?: number;
  decoration?: 'none' | 'default';
}

// TODO: Double click event

@Component({
  tag: 'ui-link',
  styleUrl: 'ui-link.scss',
  shadow: true,
})
export class UiLink implements ComponentInterface {
  linkElement: HTMLAnchorElement;

  iconElement: HTMLUiIconElement;

  @Element() el: HTMLElement;

  /**
   * Defines the link address
   */
  @Prop() href: string;

  /**
   * The target attribute specifies where to open the linked document. It can have one of the following values:
   **_self -** Default. Opens the document in the same window/tab as it was clicked
   **_blank -** Opens the document in a new window or tab
   */
  @Prop() target: '_self' | '_blank' | '' = '_self';

  @Prop() type: 'submit';

  @Prop() button: 'primary' | 'secondary' | 'cta';

  @Prop() icon: string;

  @Prop() iconOption: 'icon' | 'icon-only' | 'auto' | 'none' = 'icon';

  /**
   * The title attribute specifies extra information about an element.
   * The information is most often shown as a tooltip text when the mouse moves over the element.
   */
  @Prop() title: string;

  /**
   * This attribute is for specific contexts - when you want your link to be a dropdown as well or for when you're using a superteaser
   */
  @Prop({ reflect: true }) conversion: 'superteaser' | 'dropdown';

  @Prop() download: string;

  /**
   * Defines the amount of time the navigation to the link target takes. This is done for tracking purposes.
   */
  @Prop() delay: number;

  /**
   * Specifies if the link has an underline or not, if not defined, the default is for there to be an underline.
   */
  @Prop() decoration: 'none' | 'default' = 'default';

  /**
   * This method is an action that can be performed on the link, and it fires that component's click event.
   */
  @Method()
  async triggerClick() {
    this.linkElement.click();
  }

  private get isButton(): boolean {
    return !!this.button;
  }

  private get buttonModifier(): string {
    return `button--${this.button}`;
  }

  private fakeSubmit(event: Event) {
    const form = this.el.closest('form');
    if (form) {
      event.preventDefault();
      const fakeSubmit = document.createElement('button');
      fakeSubmit.type = 'submit';
      fakeSubmit.style.display = 'none';
      form.appendChild(fakeSubmit);
      fakeSubmit.click();
      fakeSubmit.remove();
    }
  }

  private async crossOriginDownload(event: Event) {
    event.preventDefault();
    const contentBlob = await fetch(this.href).then((res) => res.blob());
    const windowNavigator = window.navigator as any;

    if (windowNavigator && windowNavigator.msSaveOrOpenBlob) {
      windowNavigator.msSaveOrOpenBlob(contentBlob, this.download);
      return;
    }

    const url = window.URL.createObjectURL(contentBlob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.innerText = url;
    a.download = this.download;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  @Listen('click')
  onClick(event: MouseEvent) {
    // Clicked NOT on a-tag
    if (!event.composedPath().includes(this.linkElement)) {
      event.preventDefault();
      event.stopPropagation();
      this.triggerClick();
      return;
    }
    // Submit Button
    if (this.type === 'submit') {
      this.fakeSubmit(event);
    }

    if (this.download) {
      this.crossOriginDownload(event);
    }
    // Should delay page change for tracking
    if (this.delay) {
      event.preventDefault();
      setTimeout(() => {
        const linkClone = this.linkElement.cloneNode();
        const fakeDiv = document.createElement('div');
        fakeDiv.appendChild(linkClone);
        fakeDiv.querySelector('a').click();
        fakeDiv.remove();
      }, this.delay);
    }
  }

  render() {
    return (
      <Host
        class={{
          'full-width-xs': this.isButton,
        }}
      >
        <a
          ref={(el) => {
            this.linkElement = el;
          }}
          class={{
            [this.buttonModifier]: this.isButton,
            button: this.isButton,
            'no-decoration': this.decoration === 'none',
          }}
          href={this.href}
          target={this.target}
          rel={this.target === '_blank' ? 'noreferrer' : ''}
          download={this.download}
          part="ui-link_a"
          title={this.title}
        >
          {this.icon &&
          (this.iconOption === 'icon' || this.iconOption === 'icon-only') ? (
            <ui-icon
              ref={(el) => {
                this.iconElement = el;
              }}
              icon={this.icon}
              type="system"
            ></ui-icon>
          ) : null}
          {this.iconOption !== 'icon-only' && (
            <span class={this.isButton ? 'button__text' : ''}>
              <slot></slot>
            </span>
          )}
        </a>
      </Host>
    );
  }
}

versionComponent(UiLink);
